/**
 * Meta Pixel SPA helpers.
 * Bootstrap (fbq init) is in public/index.html — init only, no PageView there.
 */

import {
  buildMarketingPageViewKey,
  shouldTrackMarketingPageView,
} from './shared/marketingRouteExclusions';
import {
  HOTEL_META_PURCHASE_CONFIRMED_STATUSES,
  HOTEL_META_PURCHASE_SKIP_STATUSES,
  META_PURCHASE_RECORD,
  META_PURCHASE_STORAGE_PREFIX,
} from './metaPixel.constants';

const DEFAULT_ALLOWED_HOSTS = ['yovotrip.com', 'www.yovotrip.com'];

function metaPixelId() {
  return process.env.REACT_APP_META_PIXEL_ID;
}

function metaPixelEnabled() {
  return process.env.REACT_APP_META_PIXEL_ENABLED === 'true';
}

function metaPixelAllowedHosts() {
  return (process.env.REACT_APP_META_PIXEL_ALLOWED_HOSTS || DEFAULT_ALLOWED_HOSTS.join(','))
    .split(',')
    .map((host) => host.trim())
    .filter(Boolean);
}

/** @type {string | null} */
let lastTrackedPageKey = null;

/**
 * Dedupe key for SPA PageView tracking.
 * React Router provides a stable location.search string per navigation.
 *
 * @param {string | undefined | null} pathname
 * @param {string | undefined | null} search
 * @returns {string}
 */
export function buildMetaPageViewKey(pathname, search) {
  return buildMarketingPageViewKey(pathname, search);
}

/**
 * @returns {boolean}
 */
export function isAllowedMetaPixelHostname() {
  if (typeof window === 'undefined') {
    return false;
  }
  return metaPixelAllowedHosts().includes(window.location.hostname);
}

/**
 * @returns {boolean}
 */
export function isMetaPixelEnabled() {
  return (
    metaPixelEnabled() &&
    Boolean(metaPixelId()) &&
    isAllowedMetaPixelHostname() &&
    typeof window !== 'undefined' &&
    typeof window.fbq === 'function'
  );
}

/**
 * @param {string | undefined | null} pathname
 * @returns {boolean}
 */
export function shouldTrackMetaPageView(pathname) {
  return shouldTrackMarketingPageView(pathname);
}

/**
 * Track one PageView per distinct pathname + search.
 * Dedupes React StrictMode double-effects in development.
 *
 * @param {string | undefined | null} pathname
 * @param {string | undefined | null} search
 */
export function trackMetaPageView(pathname, search) {
  if (!isMetaPixelEnabled()) {
    return;
  }

  if (!shouldTrackMetaPageView(pathname)) {
    return;
  }

  const pageKey = buildMetaPageViewKey(pathname, search);

  if (lastTrackedPageKey === pageKey) {
    return;
  }

  lastTrackedPageKey = pageKey;
  window.fbq('track', 'PageView');
}

/** Safe wrapper — never throws, never blocks booking flow */
function safeFbq(eventName, params) {
  try {
    if (!isMetaPixelEnabled()) return;
    window.fbq('track', eventName, params);
  } catch (_) { /* pixel failure must never break app flow */ }
}

/**
 * Track a flight search event.
 * @param {{ searchString: string, contentIds: string[], value: number }} params
 */
export function trackMetaSearch({ searchString, contentIds, value }) {
  safeFbq('Search', {
    search_string: searchString,
    content_category: 'flights',
    content_ids: contentIds,
    value: value || 0,
    currency: 'INR',
  });
}

/**
 * Track viewing a specific flight's fare details.
 * @param {{ contentIds: string[], contentName: string, value: number }} params
 */
export function trackMetaViewContent({ contentIds, contentName, value }) {
  safeFbq('ViewContent', {
    content_ids: contentIds,
    content_name: contentName,
    content_type: 'product',
    value: value || 0,
    currency: 'INR',
  });
}

/**
 * Track checkout initiation (user clicks Continue to Payment).
 * @param {{ contentIds: string[], numItems: number, value: number }} params
 */
export function trackMetaInitiateCheckout({ contentIds, numItems, value }) {
  safeFbq('InitiateCheckout', {
    content_ids: contentIds,
    content_type: 'product',
    num_items: numItems || 1,
    value: value || 0,
    currency: 'INR',
  });
}

/**
 * Track when payment form is opened (Razorpay modal).
 * @param {{ contentIds: string[], value: number }} params
 */
export function trackMetaAddPaymentInfo({ contentIds, value }) {
  safeFbq('AddPaymentInfo', {
    content_ids: contentIds,
    content_type: 'product',
    value: value || 0,
    currency: 'INR',
  });
}

/**
 * First finite amount greater than zero. Zero and missing amounts are skipped
 * so a Purchase is never sent as ₹0.
 * @param {...(number|string|null|undefined)} values
 * @returns {number|null}
 */
export function positivePurchaseAmount(...values) {
  for (const value of values) {
    const amount = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isFinite(amount) && amount > 0) return amount;
  }
  return null;
}

/**
 * Amount shown as Total Paid on the hotel confirmation page.
 * Prefers the post-offer total when the booking stored one.
 * @param {object|null|undefined} pricing
 * @returns {number|null}
 */
export function resolveHotelPurchaseAmount(pricing) {
  if (!pricing || typeof pricing !== 'object') return null;
  return positivePurchaseAmount(
    pricing.customerTotalAfterDiscount,
    pricing.customerTotal,
    pricing.netAmount,
  );
}

/**
 * @param {string|null|undefined} bookingStatus
 * @returns {'send'|'skip'|'wait'}
 */
export function hotelPurchaseDecision(bookingStatus, { isPayLater = false } = {}) {
  if (isPayLater) return 'wait';
  if (HOTEL_META_PURCHASE_SKIP_STATUSES.includes(bookingStatus)) return 'skip';
  if (HOTEL_META_PURCHASE_CONFIRMED_STATUSES.includes(bookingStatus)) return 'send';
  return 'wait';
}

function normalizePurchaseOrderId(orderId) {
  if (typeof orderId !== 'string') return '';
  return orderId.trim();
}

function purchaseStorageKey(orderId) {
  return `${META_PURCHASE_STORAGE_PREFIX}${orderId}`;
}

function readPurchaseRecord(orderId) {
  const id = normalizePurchaseOrderId(orderId);
  if (!id || typeof window === 'undefined' || !window.localStorage) return null;
  try {
    return window.localStorage.getItem(purchaseStorageKey(id));
  } catch {
    return null;
  }
}

function writePurchaseRecord(orderId, value) {
  const id = normalizePurchaseOrderId(orderId);
  if (!id || typeof window === 'undefined' || !window.localStorage) return false;
  try {
    window.localStorage.setItem(purchaseStorageKey(id), value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Remember that this paid booking reached confirmation polling.
 * Ignored when Purchase was already sent for the order.
 * @param {string|null|undefined} orderId
 * @returns {boolean}
 */
export function markMetaPurchasePending(orderId) {
  const current = readPurchaseRecord(orderId);
  if (current === META_PURCHASE_RECORD.FIRED) return false;
  if (current === META_PURCHASE_RECORD.PENDING) return true;
  return writePurchaseRecord(orderId, META_PURCHASE_RECORD.PENDING);
}

/**
 * Drop a pending Purchase that must not be sent (partial flight, cancelled hotel).
 * A record that already fired is left in place.
 * @param {string|null|undefined} orderId
 */
export function releaseMetaPurchasePending(orderId) {
  const id = normalizePurchaseOrderId(orderId);
  if (!id || readPurchaseRecord(id) !== META_PURCHASE_RECORD.PENDING) return;
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(purchaseStorageKey(id));
  } catch {
    /* storage unavailable — the pending flag stays */
  }
}

export function isMetaPurchasePending(orderId) {
  return readPurchaseRecord(orderId) === META_PURCHASE_RECORD.PENDING;
}

/**
 * Send Purchase once for a pending order, using a positive paid amount.
 * Leaves the pending flag in place when the amount is missing or the pixel is off,
 * so a later confirmation load can still send it.
 * @param {{ orderId: string, value: number|string, contentIds?: string[], quantity?: number }} params
 * @returns {boolean}
 */
export function trackPendingMetaPurchase({ orderId, value, contentIds, quantity } = {}) {
  const id = normalizePurchaseOrderId(orderId);
  if (!id || !isMetaPurchasePending(id)) return false;

  const amount = positivePurchaseAmount(value);
  if (amount == null || !isMetaPixelEnabled()) return false;

  const ids = (contentIds || []).filter(Boolean);
  trackMetaPurchase({
    value: amount,
    contentIds: ids.length ? ids : [id],
    quantity: quantity || 1,
  });
  writePurchaseRecord(id, META_PURCHASE_RECORD.FIRED);
  return true;
}

/**
 * Track successful purchase. No-ops unless value is a positive amount.
 * @param {{ value: number, contentIds: string[], quantity: number }} params
 */
export function trackMetaPurchase({ value, contentIds, quantity }) {
  const amount = positivePurchaseAmount(value);
  if (amount == null) return;

  safeFbq('Purchase', {
    value: amount,
    currency: 'INR',
    contents: (contentIds || []).map((id) => ({ id, quantity: quantity || 1 })),
    content_type: 'product',
  });
}
